import { IAgentQAGroupedItem } from 'types/agentQA';

interface CSVRow {
  type: string;
  id: string;
  translation_id: string;
  us_text: string;
  current_translation: string;
  freelance_pattern: string;
  freelance_text: string;
  ai_pattern: string;
  ai_text: string;
  difference: string;
  consistency: string;
  referenced_rbs: string;
  xx_text: string;
  xx_status: string;
  action: string;
}

interface TranslationParts {
  pattern: string;
  text: string;
}

function getTranslation(result: any): TranslationParts | null {
  if (!result) return null;
  try {
    const obj = typeof result === 'string' ? JSON.parse(result) : result;
    if (obj.segments) return null; // Skip dialog/segment items
    if (obj.translation == null) return null;
    return {
      pattern: String(obj.translation),
      text: typeof obj.translation_text === 'string' ? obj.translation_text : '',
    };
  } catch {
    return null;
  }
}

function escapeCsvValue(value: string): string {
  return '"' + value.replace(/"/g, '""') + '"';
}

function toCsvLine(values: string[]): string {
  return values.map(escapeCsvValue).join(',');
}

/**
 * Compute a word-level diff between two strings.
 * Returns a human-readable string like: "[old] → [new] | (added) → [word]"
 */
function computeWordDiff(oldText: string, newText: string): string {
  const oldWords = oldText.split(/\s+/).filter((w) => w.length > 0);
  const newWords = newText.split(/\s+/).filter((w) => w.length > 0);

  // LCS table
  const m = oldWords.length;
  const n = newWords.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find diff operations
  const changes: string[] = [];
  let i = m;
  let j = n;

  // Collect operations in reverse, then reverse at end
  const ops: Array<{ type: 'equal' | 'remove' | 'add'; word: string }> = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      ops.push({ type: 'equal', word: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.push({ type: 'add', word: newWords[j - 1] });
      j--;
    } else {
      ops.push({ type: 'remove', word: oldWords[i - 1] });
      i--;
    }
  }

  ops.reverse();

  // Merge consecutive removes/adds into change pairs
  let idx = 0;
  while (idx < ops.length) {
    if (ops[idx].type === 'equal') {
      idx++;
      continue;
    }

    // Collect consecutive removes
    const removed: string[] = [];
    while (idx < ops.length && ops[idx].type === 'remove') {
      removed.push(ops[idx].word);
      idx++;
    }

    // Collect consecutive adds
    const added: string[] = [];
    while (idx < ops.length && ops[idx].type === 'add') {
      added.push(ops[idx].word);
      idx++;
    }

    if (removed.length > 0 && added.length > 0) {
      changes.push(`[${removed.join(' ')}] → [${added.join(' ')}]`);
    } else if (removed.length > 0) {
      changes.push(`[${removed.join(' ')}] → (removed)`);
    } else if (added.length > 0) {
      changes.push(`(added) → [${added.join(' ')}]`);
    }
  }

  return changes.join(' | ');
}

type CitedExample = { source: string; target: string; translation_id: number; source_text?: string; target_text?: string };

export function exportAgentQACSV(gridData: IAgentQAGroupedItem[], jobName: string): void {
  const dialectCode = gridData.find((g) => g.item_info?.dialect_code)?.item_info?.dialect_code ?? 'XX';
  const header = ['type', 'id', 'translation_id', 'us_text', 'current_translation', 'freelance_pattern', 'freelance_text', 'ai_pattern', 'ai_text', 'difference', 'consistency', 'referenced_rbs', 'action', `${dialectCode}_text`, `${dialectCode}_status`];
  const rows: CSVRow[] = [];
  const globalRBsSeen = new Map<string, CitedExample>(); // deduped RBs across all groups

  for (const group of gridData) {
    const info = group.item_info;
    if (!info) continue;

    // Get freelance version (expected_result) - same across all runs
    const firstRun = group.runs[0];
    if (!firstRun) continue;

    const freelanceVersion = getTranslation(firstRun.expected_result);
    if (freelanceVersion === null) continue; // Skip dialog/segment items

    // Collect distinct AI versions from completed runs, keyed by pattern (with cited_examples from first matching run)
    const aiVersionCounts = new Map<string, number>();
    const aiVersionTexts = new Map<string, string>();
    const aiVersionCitedExamples = new Map<string, CitedExample[]>();
    const totalRuns = group.runs.length;

    for (const run of group.runs) {
      const aiVersion = getTranslation(run.actual_result);
      if (aiVersion === null) continue; // Skip pending/error runs
      const key = aiVersion.pattern;
      aiVersionCounts.set(key, (aiVersionCounts.get(key) ?? 0) + 1);
      if (!aiVersionTexts.has(key)) {
        aiVersionTexts.set(key, aiVersion.text);
      }
      // Collect cited_examples from the first run that produced this AI version
      if (!aiVersionCitedExamples.has(key)) {
        try {
          const actualObj = typeof run.actual_result === 'string' ? JSON.parse(run.actual_result) : run.actual_result;
          const raw: any[] = Array.isArray(actualObj?.cited_examples) ? actualObj.cited_examples : [];
          const seen = new Set<string>();
          const normalized: CitedExample[] = [];
          for (const ex of raw) {
            const entry: CitedExample =
              typeof ex === 'string'
                ? { source: ex, target: '', translation_id: 0 }
                : {
                    source: String(ex?.source ?? ''),
                    target: String(ex?.target ?? ''),
                    translation_id: Number(ex?.translation_id ?? 0),
                    source_text: ex?.source_text ? String(ex.source_text) : undefined,
                    target_text: ex?.target_text ? String(ex.target_text) : undefined,
                  };
            if (!seen.has(entry.source)) {
              seen.add(entry.source);
              normalized.push(entry);
            }
          }
          aiVersionCitedExamples.set(key, normalized);
        } catch {
          aiVersionCitedExamples.set(key, []);
        }
      }
    }

    if (aiVersionCounts.size === 0) continue;

    // Sort by count descending (most common first)
    const sortedVersions = Array.from(aiVersionCounts.entries()).sort((a, b) => b[1] - a[1]);

    let isFirstRow = true;
    const groupRBsSeen = new Map<string, CitedExample>(); // RBs for this group

    for (const [aiPattern, count] of sortedVersions) {
      // Only include rows where AI differs from freelance
      if (aiPattern === freelanceVersion.pattern) continue;

      const aiText = aiVersionTexts.get(aiPattern) ?? '';
      const difference = computeWordDiff(freelanceVersion.pattern, aiPattern);
      const consistency = `${count}/${totalRuns}`;
      const citedExamples = aiVersionCitedExamples.get(aiPattern) ?? [];
      const referencedRbs = citedExamples
        .filter((ex) => ex.translation_id > 0)
        .map((ex) => String(ex.translation_id))
        .join(', ');

      // Collect unique RBs for this group
      for (const ex of citedExamples) {
        const key = ex.translation_id > 0 ? String(ex.translation_id) : ex.source;
        if (!groupRBsSeen.has(key)) {
          groupRBsSeen.set(key, ex);
        }
      }

      if (isFirstRow) {
        rows.push({
          type: 'QA',
          id: String(info.entity_id ?? ''),
          translation_id: String(info.translation_id ?? ''),
          us_text: info.us_translation ?? '',
          current_translation: info.target_translation ?? '',
          freelance_pattern: freelanceVersion.pattern,
          freelance_text: freelanceVersion.text,
          ai_pattern: aiPattern,
          ai_text: aiText,
          difference,
          consistency,
          referenced_rbs: referencedRbs,
          xx_text: '',
          xx_status: '',
          action: '',
        });
        isFirstRow = false;
      } else {
        rows.push({
          type: 'QA',
          id: '',
          translation_id: '',
          us_text: '',
          current_translation: '',
          freelance_pattern: '',
          freelance_text: '',
          ai_pattern: aiPattern,
          ai_text: aiText,
          difference,
          consistency,
          referenced_rbs: referencedRbs,
          xx_text: '',
          xx_status: '',
          action: '',
        });
      }
    }

    // Add RB rows for this group, skipping any already added in a previous group
    for (const [key, ex] of groupRBsSeen) {
      if (globalRBsSeen.has(key)) continue;
      globalRBsSeen.set(key, ex);
      rows.push({
        type: 'RB',
        id: '',
        translation_id: ex.translation_id > 0 ? String(ex.translation_id) : '',
        us_text: ex.source_text ?? ex.source,
        current_translation: ex.target_text ?? ex.target,
        freelance_pattern: '',
        freelance_text: '',
        ai_pattern: '',
        ai_text: '',
        difference: '',
        consistency: '',
        referenced_rbs: '',
        xx_text: '',
        xx_status: '',
        action: '',
      });
    }
  }

  if (rows.length === 0) {
    alert('No differences found to export.');
    return;
  }

  // Sort by id (group rows stay together since subsequent rows have empty id)
  // Already in grid order, no additional sort needed

  const csvLines = [toCsvLine(header)];
  for (const row of rows) {
    csvLines.push(
      toCsvLine([
        row.type,
        row.id,
        row.translation_id,
        row.us_text,
        row.current_translation,
        row.freelance_pattern,
        row.freelance_text,
        row.ai_pattern,
        row.ai_text,
        row.difference,
        row.consistency,
        row.referenced_rbs,
        row.action,
        row.xx_text,
        row.xx_status,
      ]),
    );
  }

  const csvContent = csvLines.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeName = (jobName || 'AgentQA').replace(/\s+/g, '_');
  link.download = `${safeName}_QA_report.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
